import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Mail, Phone, Linkedin, Pencil, Trash2, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Empty, fmtD, pInitials, REL_STATUSES, REL_PRIORITIES, COLLAB_AREAS } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

export function NextActionDialog({ cid, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ title: "", due_date: "", due_time: "09:00", type: "task" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ title: "", due_date: "", due_time: "09:00", type: "task" }); }, [open]);
  const submit = async () => {
    if (!form.title.trim() || !form.due_date || busy) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${cid}/next-action`, form);
      toast.success(tfr("Action planifiée — visible dans le calendrier"));
      onOpenChange(false); onCreated?.();
    } catch { toast.error(tfr("Impossible de planifier l'action")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="next-action-dialog">
        <DialogHeader><DialogTitle className="text-base">{tfr("Planifier une action")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Titre</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tfr("Ex. Relancer le directeur sportif")} className="h-10 mt-1" data-testid="next-action-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-10 mt-1" data-testid="next-action-date" /></div>
            <div><Label className="text-xs">Heure</Label>
              <Input type="time" value={form.due_time} onChange={(e) => setForm({ ...form, due_time: e.target.value })} className="h-10 mt-1" /></div>
          </div>
        </div>
        <Button onClick={submit} disabled={!form.title.trim() || !form.due_date || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="next-action-submit">
          Planifier
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_CONTACT = { name: "", role: "", department: "", email: "", phone: "", linkedin: "", other_link: "", language: "", notes: "", photo_b64: "", is_primary: false };
const LANGUAGES = ["Français", "Anglais", "Espagnol", "Italien", "Allemand", "Portugais", "Arabe", "Autre"].map((s) => tfr(s));

export default function TeamRelations({ cid, club, health, onChanged }) {
  const [contacts, setContacts] = useState(null);
  const [dlg, setDlg] = useState(null); // {contact?} for edit, {} for create
  const [confirmDel, setConfirmDel] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/clubs/${cid}/contacts`).then((r) => setContacts(r.data)).catch(() => setContacts([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const patchRel = async (payload) => {
    try { await api.patch(`/clubs/${cid}/relationship`, payload); onChanged(); }
    catch { toast.error(tfr("Mise à jour impossible")); }
  };
  const toggleArea = (a) => {
    const cur = club.collaboration_areas || [];
    patchRel({ collaboration_areas: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] });
  };
  const del = async () => {
    try {
      await api.delete(`/clubs/${cid}/contacts/${confirmDel.id}`);
      toast.success(tfr("Contact supprimé")); setConfirmDel(null); load(); onChanged();
    } catch { toast.error(tfr("Suppression impossible")); }
  };

  if (!contacts) return <div className="h-48 rounded-2xl bg-muted/60 animate-pulse" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
      <Section title={`Contacts (${contacts.length})`} testid="rel-contacts"
        action={<Button size="sm" onClick={() => setDlg({})} className="h-8 gap-1 bg-athlynx text-white hover:bg-athlynx/90 text-xs" data-testid="rel-add-contact"><Plus className="h-3 w-3" /> {tfr("Ajouter un contact")}</Button>}>
        {contacts.length === 0 ? (
          <Empty text={tfr("Aucun contact pour cette équipe. Identifiez vos interlocuteurs clés.")} cta={tfr("Ajouter un contact")} onClick={() => setDlg({})} testid="rel-contacts-empty" />
        ) : (
          <div className="divide-y divide-border/70">
            {contacts.map((c, i) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`rel-contact-${i}`}>
                {c.photo_b64 ? (
                  <img src={c.photo_b64} alt={c.name} className="h-9 w-9 rounded-full object-cover shrink-0" />
                ) : (
                  <span className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">{pInitials(c.name)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {c.name}
                    {c.is_primary && <span className="text-[9px] uppercase tracking-wider rounded-full border border-athlynx/30 bg-athlynx/10 text-athlynx px-1.5 py-0.5">Principal</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{[c.role, c.department].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <div className="flex items-center gap-1.5 ml-auto">
                  {c.email && <a href={`mailto:${c.email}`} aria-label={`Email ${c.name}`} className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Mail className="h-3.5 w-3.5" /></a>}
                  {c.phone && <a href={`tel:${c.phone}`} aria-label={`Appeler ${c.name}`} className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Phone className="h-3.5 w-3.5" /></a>}
                  {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Linkedin className="h-3.5 w-3.5" /></a>}
                  <button onClick={() => setDlg({ contact: c })} aria-label={`${tfr("Modifier")} ${c.name}`} data-testid={`rel-contact-edit-${i}`} className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setConfirmDel(c)} aria-label={`${tfr("Supprimer")} ${c.name}`} className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="space-y-4">
        <Section title="Relation" testid="rel-panel">
          <div className="space-y-3">
            <div><Label className="text-xs">{tfr("Statut de la relation")}</Label>
              <Select value={club.relationship_status || "Prospect"} onValueChange={(v) => patchRel({ relationship_status: v })}>
                <SelectTrigger className="h-10 mt-1 text-xs" data-testid="rel-status"><SelectValue /></SelectTrigger>
                <SelectContent>{REL_STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">{tfr("Priorité")}</Label>
              <Select value={club.priority || "Normale"} onValueChange={(v) => patchRel({ priority: v })}>
                <SelectTrigger className="h-10 mt-1 text-xs" data-testid="rel-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{REL_PRIORITIES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Relation depuis</Label>
              <Input type="date" value={club.relationship_since || ""} onChange={(e) => patchRel({ relationship_since: e.target.value })} className="h-10 mt-1" data-testid="rel-since" /></div>
            <Button size="sm" onClick={() => setActionOpen(true)} className="w-full h-9 bg-athlynx text-white hover:bg-athlynx/90 text-xs" data-testid="rel-plan-action">
              {tfr("Planifier la prochaine action")}
            </Button>
          </div>
        </Section>

        <Section title={tfr("Axes de collaboration")} testid="rel-areas">
          <div className="flex flex-wrap gap-1.5">
            {COLLAB_AREAS.map((a) => {
              const on = (club.collaboration_areas || []).includes(a);
              return (
                <button key={a} onClick={() => toggleArea(a)} data-testid={`rel-area-${a}`}
                  className={cn("text-[11px] rounded-full border px-2.5 py-1 transition-colors",
                    on ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
                  {a}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title={`${tfr("Santé de la relation :")} ${health.score} /100`} testid="rel-health"
          action={<button onClick={() => setWhyOpen((v) => !v)} className="text-xs text-athlynx hover:underline">{tfr("Pourquoi ce score ?")}</button>}>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className={cn("h-full rounded-full", health.score >= 70 ? "bg-emerald-500" : health.score >= 40 ? "bg-amber-500" : "bg-rose-500")} style={{ width: `${health.score}%` }} />
          </div>
          {whyOpen && (
            <ul className="mt-2 space-y-1">
              {health.reasons.map((r, i) => <li key={i} className="text-xs text-muted-foreground">• {r}</li>)}
            </ul>
          )}
        </Section>
      </div>

      {dlg && <ContactDialog cid={cid} clubName={club.name} contact={dlg.contact} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); onChanged(); }} />}
      <NextActionDialog cid={cid} open={actionOpen} onOpenChange={setActionOpen} onCreated={onChanged} />

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">{tfr("Supprimer ce contact ?")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{tfr("Cette action est définitive.")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>{tfr("Annuler")}</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={del} data-testid="rel-contact-del-confirm">{tfr("Supprimer")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactDialog({ cid, clubName, contact, onClose, onSaved }) {
  const [mode, setMode] = useState("new");
  const [form, setForm] = useState(contact ? { ...EMPTY_CONTACT, ...contact } : EMPTY_CONTACT);
  const [tagsText, setTagsText] = useState(contact ? (contact.tags || []).join(", ") : "");
  const [assoc, setAssoc] = useState(false);
  const [more, setMore] = useState(!!contact);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [existing, setExisting] = useState([]);
  const [rows, setRows] = useState(null);
  const [dupFlags, setDupFlags] = useState([]);
  const [sel, setSel] = useState({});
  const [importSource, setImportSource] = useState("csv");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ROLES = ["Directeur sportif", "Recruteur", "Scout", "Président", "Entraîneur", "Responsable recrutement", "Responsable formation", "Juridique", "Médical", "Agent", "Intermédiaire", "Sponsoring / Partenariats", "Communication"].map((s) => tfr(s));

  useEffect(() => {
    if (contact || (form.name || "").length < 2) { setExisting([]); return; }
    const t = setTimeout(() => api.get(`/clubs/${cid}/contacts-search`, { params: { q: form.name } })
      .then((r) => setExisting((r.data || []).filter((c) => c.club_id !== cid))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [form.name, cid, contact]);

  const submit = async (force = false) => {
    if (!form.name.trim() || busy) return;
    setBusy(true);
    try {
      const payload = { name: form.name, role: form.role, department: form.department, email: form.email, phone: form.phone,
        linkedin: form.linkedin, other_link: form.other_link, language: form.language, notes: form.notes,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean), photo_b64: form.photo_b64 || "",
        is_primary: form.is_primary, force };
      if (contact?.id) await api.patch(`/clubs/${cid}/contacts/${contact.id}`, payload);
      else await api.post(`/clubs/${cid}/contacts`, payload);
      toast.success(contact ? tfr("Contact modifié") : tfr("Contact ajouté"));
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.existing) setDup(d.existing);
      else toast.error(typeof d === "string" ? d : "Enregistrement impossible");
    } finally { setBusy(false); }
  };

  const parseVcf = (text) => [...text.matchAll(/BEGIN:VCARD[\s\S]*?END:VCARD/g)].map((m) => {
    const b = m[0];
    const g = (re) => (b.match(re) || [])[1]?.trim() || "";
    return { name: g(/FN[^:]*:(.+)/), phone: g(/TEL[^:]*:(.+)/), email: g(/EMAIL[^:]*:(.+)/), role: g(/TITLE[^:]*:(.+)/), organization: g(/ORG[^:]*:(.+)/) };
  }).filter((c) => c.name);

  const parseCsv = (text) => {
    const splitLine = (l, d) => {
      const out = []; let cur = "", q = false;
      for (const ch of l) {
        if (ch === '"') q = !q;
        else if (ch === d && !q) { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur);
      return out.map((x) => x.trim());
    };
    const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const delim = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
    const heads = splitLine(lines[0], delim).map((h) => h.toLowerCase());
    const find = (pred) => heads.findIndex(pred);
    const iFn = find((h) => h.includes("first name") || h.includes("prénom") || h.includes("prenom"));
    const iLn = find((h) => h.includes("last name") || h === "nom" || h.includes("nom de famille"));
    const iN = find((h) => (h.includes("name") || h.includes("nom")) && !h.includes("first") && !h.includes("last") && !h.includes("middle") && !h.includes("organi") && !h.includes("prénom") && !h.includes("prenom") && !h.includes("file"));
    const pick = (kw) => {
      const v = find((h) => kw.some((k) => h.includes(k)) && h.includes("value"));
      return v >= 0 ? v : find((h) => kw.some((k) => h.includes(k)) && !h.includes("type") && !h.includes("label"));
    };
    const iP = pick(["phone", "tél", "tel", "mobile"]), iE = pick(["email", "e-mail", "mail"]);
    const iR = find((h) => h.includes("title") || h.includes("fonction") || h.includes("rôle") || h.includes("role") || h.includes("poste"));
    const iO = find((h) => h.includes("company") || h.includes("organi") || h.includes("société") || h.includes("club"));
    const cell = (c, i) => (i >= 0 && c[i]) ? c[i] : "";
    return lines.slice(1).map((l) => {
      const c = splitLine(l, delim);
      if (heads.length > 1 && c.length < 2) return null;
      const name = (iFn >= 0 || iLn >= 0)
        ? [cell(c, iFn), cell(c, iLn)].filter(Boolean).join(" ").trim()
        : cell(c, iN);
      return { name, phone: cell(c, iP), email: cell(c, iE), role: cell(c, iR), organization: cell(c, iO) };
    }).filter((c) => c && c.name);
  };

  const checkDupes = async (parsed) => {
    try {
      const r = await api.post(`/clubs/${cid}/contacts/import`, { contacts: parsed, dry_run: true });
      return (r.data.results || []).map((x) => x.status === "existing");
    } catch { return parsed.map(() => false); }
  };

  const showReview = async (parsed, source) => {
    const list = parsed.slice(0, 100);
    setImportSource(source);
    const flags = await checkDupes(list);
    setRows(list); setDupFlags(flags);
    setSel(Object.fromEntries(list.map((_, i) => [i, !flags[i]])));
  };

  const onFile = async (f) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Fichier trop volumineux (2 Mo max)"); return; }
    const isVcf = f.name.toLowerCase().endsWith(".vcf");
    let parsed = [];
    try {
      const text = await f.text();
      parsed = isVcf ? parseVcf(text) : parseCsv(text);
    } catch { /* unreadable file */ }
    if (!parsed.length) { toast.error(tfr("Aucun contact lisible dans ce fichier")); return; }
    await showReview(parsed, isVcf ? "vcard" : "csv");
  };

  const pickDevice = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });
      const parsed = picked.map((c) => ({ name: (c.name || [])[0] || "", phone: (c.tel || [])[0] || "", email: (c.email || [])[0] || "", role: "", organization: "" })).filter((c) => c.name);
      if (parsed.length) await showReview(parsed, "device_import");
    } catch { /* user cancelled */ }
  };

  const doImport = async () => {
    const chosen = (rows || []).filter((_, i) => sel[i]);
    if (!chosen.length || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/clubs/${cid}/contacts/import`, { contacts: chosen, source: importSource });
      toast.success(`${r.data.created} ${tfr("contact(s) importé(s)")}${r.data.skipped ? ` · ${r.data.skipped} ${tfr("déjà existant(s)")}` : ""}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible"); } finally { setBusy(false); }
  };

  const onPhoto = (f) => {
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const s = 128; c.width = s; c.height = s;
      const m = Math.min(img.width, img.height);
      c.getContext("2d").drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, s, s);
      set("photo_b64", c.toDataURL("image/jpeg", 0.8));
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => toast.error("Image illisible");
    img.src = URL.createObjectURL(f);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="text-base">{contact ? tfr("Modifier le contact") : tfr("Ajouter un contact")}</DialogTitle></DialogHeader>
        {!contact && (
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {[["new", tfr("Nouveau contact")], ["import", "Importer mes contacts"]].map(([m, l]) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`contact-mode-${m}`}
                className={cn("flex-1 h-8 rounded-md text-xs font-medium transition-colors", mode === m ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
          </div>
        )}

        {mode === "new" ? (
          <>
            <div className="space-y-3">
              <div><Label className="text-xs">Nom complet <span className="text-athlynx">*</span></Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex. Olivier Garcia" className="h-10 mt-1" data-testid="contact-name" />
                {existing.length > 0 && (
                  <div className="mt-1.5 rounded-lg border border-border bg-secondary/40 p-2" data-testid="contact-existing">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Contacts existants</p>
                    {existing.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-2 py-1 text-xs">
                        <span className="min-w-0 truncate">{c.name} · {c.role || "—"} <span className="text-muted-foreground">({c.club_name})</span></span>
                        <button onClick={() => { setForm({ ...EMPTY_CONTACT, name: c.name, role: c.role, email: c.email, phone: c.phone, linkedin: c.linkedin }); setAssoc(true); }}
                          className="text-athlynx font-medium shrink-0" data-testid={`contact-reuse-${c.id}`}>Associer ici</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div><Label className="text-xs">{tfr("Rôle / Fonction")}</Label>
                <Input list="contact-roles" value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="Ex. Directeur sportif" className="h-10 mt-1" data-testid="contact-role" />
                <datalist id="contact-roles">{ROLES.map((r) => <option key={r} value={r} />)}</datalist></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">{tfr("Téléphone")}</Label>
                  <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 6 12 34 56 78" className="h-10 mt-1" data-testid="contact-phone" /></div>
                <div><Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="nom@club.fr" className="h-10 mt-1" data-testid="contact-email" /></div>
              </div>
              {!form.phone && !form.email && <p className="text-[11px] text-muted-foreground">{tfr("Vous pourrez ajouter ses coordonnées plus tard.")}</p>}
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.is_primary} onChange={(e) => set("is_primary", e.target.checked)} className="h-4 w-4 accent-athlynx" data-testid="contact-primary" />
                Contact principal <Star className="h-3 w-3 text-amber-500" aria-hidden />
              </label>
              <button type="button" onClick={() => setMore((v) => !v)} className="text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="contact-more">
                {more ? tfr("− Moins de détails") : tfr("+ Plus de détails")}
              </button>
              {more && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">{tfr("Département")}</Label>
                    <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Ex. Recrutement" className="h-10 mt-1" data-testid="contact-department" /></div>
                  <div><Label className="text-xs">Langue</Label>
                    <Select value={form.language || ""} onValueChange={(v) => set("language", v)}>
                      <SelectTrigger className="h-10 mt-1 text-xs" data-testid="contact-language"><SelectValue placeholder={tfr("Choisir…")} /></SelectTrigger>
                      <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{tfr(l)}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">LinkedIn</Label>
                    <Input value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/…" className="h-10 mt-1" data-testid="contact-linkedin" /></div>
                  <div><Label className="text-xs">Autre lien</Label>
                    <Input value={form.other_link || ""} onChange={(e) => set("other_link", e.target.value)} placeholder="https://…" className="h-10 mt-1" data-testid="contact-other-link" /></div>
                  <div className="col-span-2"><Label className="text-xs">{tfr("Tags (séparés par des virgules)")}</Label>
                    <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder={tfr("Ex. décideur, recrutement")} className="h-10 mt-1" data-testid="contact-tags" /></div>
                  <div className="col-span-2"><Label className="text-xs">Notes (500 car. max)</Label>
                    <Input value={form.notes || ""} maxLength={500} onChange={(e) => set("notes", e.target.value)} placeholder={tfr("Ex. Rencontré au tournoi de Lyon…")} className="h-10 mt-1" data-testid="contact-notes" /></div>
                  <div className="col-span-2">
                    <Label className="text-xs">Photo</Label>
                    <div className="flex items-center gap-3 mt-1">
                      {form.photo_b64 && <img src={form.photo_b64} alt={tfr("Aperçu")} className="h-10 w-10 rounded-full object-cover" data-testid="contact-photo-preview" />}
                      <Input type="file" accept="image/*" onChange={(e) => onPhoto(e.target.files?.[0])} className="h-10 pt-2 flex-1" data-testid="contact-photo" />
                      {form.photo_b64 && <button type="button" onClick={() => set("photo_b64", "")} className="text-xs text-muted-foreground hover:text-red-500">Retirer</button>}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {dup && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs" data-testid="contact-duplicate">
                <p className="font-medium">{tfr("Ce contact semble déjà exister.")}</p>
                <p className="text-muted-foreground mt-0.5">{dup.name} · {dup.role || "—"} · {dup.phone || dup.email || ""}</p>
                <div className="flex gap-2 mt-2">
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setDup(null)}>{tfr("Annuler")}</Button>
                  <Button size="sm" className="h-8 text-xs bg-athlynx text-white hover:bg-athlynx/90" onClick={() => { setDup(null); submit(true); }} data-testid="contact-force">{tfr("Créer quand même")}</Button>
                </div>
              </div>
            )}
            <Button onClick={() => submit(assoc)} disabled={!form.name.trim() || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="contact-submit">
              {contact ? tfr("Enregistrer") : tfr("Ajouter le contact")}
            </Button>
          </>
        ) : (
          <div className="space-y-3" data-testid="contact-import">
            <p className="text-xs text-muted-foreground">{tfr("Sélectionnez des contacts existants et ajoutez uniquement ceux qui sont utiles à votre réseau Athlynx.")}</p>
            {!rows ? (
              <div className="space-y-2">
                {"contacts" in navigator && "select" in (navigator.contacts || {}) && (
                  <Button variant="outline" onClick={pickDevice} className="w-full h-10" data-testid="contact-device-picker">{tfr("Choisir dans mes contacts")}</Button>
                )}
                <div>
                  <Label className="text-xs">{tfr("Importer un fichier de contacts (.vcf ou .csv)")}</Label>
                  <Input type="file" accept=".vcf,.csv" onChange={(e) => onFile(e.target.files?.[0])} className="h-10 mt-1 pt-2" data-testid="contact-import-file" />
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs">
                  <span>{rows.length} contact(s) trouvé(s) · vers <span className="font-medium">{clubName}</span></span>
                  <button className="text-muted-foreground hover:text-foreground" onClick={() => setSel(Object.fromEntries(rows.map((_, i) => [i, !dupFlags[i]])))}>{tfr("Tout sélectionner")}</button>
                </div>
                <div className="max-h-[240px] overflow-y-auto divide-y divide-border/60 rounded-lg border border-border">
                  {rows.map((c, i) => (
                    <label key={i} className="flex items-center gap-2.5 px-2.5 py-2 text-xs cursor-pointer hover:bg-accent/50" data-testid={`import-row-${i}`}>
                      <input type="checkbox" checked={!!sel[i]} onChange={(e) => setSel((s) => ({ ...s, [i]: e.target.checked }))} className="h-4 w-4 accent-athlynx" />
                      <span className="min-w-0 flex-1"><span className="block font-medium truncate">{c.name}</span>
                        <span className="block text-muted-foreground truncate">{[c.phone, c.email, c.organization].filter(Boolean).join(" · ")}</span></span>
                      {dupFlags[i] && <span className="shrink-0 text-[10px] rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5" data-testid={`import-dup-${i}`}>{tfr("Déjà existant")}</span>}
                    </label>
                  ))}
                </div>
                <Button onClick={doImport} disabled={busy || !Object.values(sel).some(Boolean)} className="w-full bg-athlynx text-white hover:bg-athlynx/90" data-testid="contact-import-submit">
                  {busy ? "Importation…" : `Importer ${Object.values(sel).filter(Boolean).length} contact(s)`}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
